import {
  T,
  always,
  equals,
  cond,
  map,
  pathOr,
  sum,
  defaultTo,
  take,
  split,
  join,
  length,
  subtract,
  toString,
  includes,
  any,
  add,
  trim,
  toLower,
} from 'ramda';
import { formatCurrencyFn, formatCurrencyWithPrecisionFn, formatRoundedCurrencyFn, unFormatCurrencyFnNum, unFormatCurrencyInCentsFnNum } from 'src/utilities/formatCurrency';
import { getNumberOfYearsAndMonths } from 'src/utilities/formatDate';
import { fullName } from 'src/utilities/naming';
import { isArray, isString } from 'src/utilities/types';
import { getIncrementDateByDays, getDaysFromDate, getMonthsFromDate, calculateDueDateWithOffset } from 'src/utilities/date';
import { isNotEmpty } from 'src/utilities/hasValue';
import { generateRandomEmail } from 'src/utilities/borrowers';
import { capitalizeAllWords } from 'src/utilities/capitalize';
/**
convertorFunctions is used to map convertor function which given name
if convertor name doesn't match then return doNothing function
@param convertor { string } unique name of convertor function name

Its smilar like formatter unlike return function can get n number of args
based on convertor function
*/
export const convertorFunctions = (convertor: string) => {
  return cond([
    [equals('TO_FULL_NAME'), always(fullName)],
    [equals('MONTHS_YEARS_TO_MONTHS'), always(monthAndYearsToMonthsCount)],
    [equals('STRING_FORMATTER'), always((a: any) => String(a))],
    [equals('STRING_FORMATTER'), always((a: any) => String(a))],
    [equals('BOOL_FORMATTER'), always((a: any) => Boolean(a))],
    [equals('NUMBER_FORMATTER'), always((a: any) => Number(a))],
    [equals('EMPLOYMENT_YEARS'), always(getEmploymentYears)],
    [equals('EMPLOYMENT_MONTHS'), always(getEmploymentMonths)],
    [equals('EMPLOYMENT_INCOME'), always(calculateTotalIncome)],
    [equals('EMPLOYMENT_INCOME_PRECISION'), always(calculateTotalIncomeInDecimals)],
    [equals('SPLIT_AND_SAVE_FORMATTER'), always(splitAndSave)],
    [equals('CONCAT'), always(concatArgs)],
    [equals('CURRENCY_FORMATTER'), always(formatCurrencyFn)],
    [equals('SUM_UP'), always(sumUp)],
    [equals('takeFromListOrCommaString'), always(takeFromListOrStringSeperatedBy(','))],
    [equals('takeFromListOrPipeString'), always(takeFromListOrStringSeperatedBy('|'))],
    [equals('lengthFromListOrCommaString'), always(lengthFromListOrStringSeperatedBy(','))],
    [equals('SUBTRACT'), always(subtractor)],
    [equals('ROUNDED_SUBTRACT'), always(roundedSubtractor)],
    [equals('ROUNDED_SUM_UP'), always(roundedSumUp)],
    [equals('DISBURSEMENT_ACCOUNT_TYPE'), always(getDisbursementAccountType)],
    [equals('INCREMENT_DATE_BY_DAYS'), always(getIncrementDateByDays)],
    [equals('INCREMENT_DATE_BY_CUTOFF'), always(calculateDueDateWithOffset)],

    [equals('GET_DAYS_FROM_DATE'), always(getDaysFromDate)],
    [equals('GET_MONTHS_FROM_DATE'), always(getMonthsFromDate)],
    [equals('RANDOM_DIGITS_WITH_PREFIX'), always(getRandomDigits)],
    [equals('CONCAT_RANDOM_DIGITS'), always(appendRandomDigits)],
    [equals('GENERATE_RANDOM_EMAIL'), always(generateRandomEmail)],
    [equals('CAPITALIZE'), always((data: string) => capitalizeAllWords(toLower(trim(data))))],
    [T, always((value: any) => value)],
  ])(convertor)
}




/**
 * Splits a string by a hyphen and returns the value at a specified index.
 *
 * @param keyString - The string to be split.
 * @param index - The index of the value to be returned.
 * @returns The value at the specified index after splitting the string by a hyphen.
 *          If the index is out of bounds or the string is empty, returns an empty string.
 * @example
 * // Returns "John"
 * console.log(splitAndSave("John-Doe", 0));

 */
export const splitAndSave = (keyString: string, index: number) => {
  return keyString ? trim(pathOr("", [index], split("-", keyString))) : "";
}

/**
 * takeFromListOrStringSeperatedBy is used to take value from List or comma [given character (, | : ;)] separated string
 * @param count { number } number of items needs to take from list or string list
 * @param separator { string } number of months
returns total number of months on given years + months
*/

export const takeFromListOrStringSeperatedBy = (separator: string = ',') => {
  return (count: number, value: any) => {
    if (isNaN(count)) {
      return value
    }
    return cond([
      [isString, () => {
        return join(separator, take(Number(count), split(separator, value)))
      }],
      [isArray, () => take(Number(count), value)],
      [T, always(value)],
    ])(value)
  }
}


/**
 * lengthFromListOrStringSeperatedBy is used to take value from List or comma [given character (, | : ;)] separated string
 * @param value { number } number of items needs to take from list or string list
 * @param separator { string } number of months
returns total number of months on given years + months
*/

export const lengthFromListOrStringSeperatedBy = (separator: string = ',') => {
  return (value: any) => {
    return cond([
      [isString, () => `${length(split(separator, value))}`],
      [isArray, () => length(value)],
      [T, always(0)],
    ])(value)
  }
}

/**
monthAndYearsToMonthsCount converts the given months and years into months count
@param years { string } number of years
@param months { string } number of months
returns total number of months on given years + months
*/
const monthAndYearsToMonthsCount = (years: any, months: any) => {
  const year = Number(years)
  const month = Number(months)
  return String((year * 12) + month)
}

/**
calculateTotalIncome to sum the give employment splitted incomes to total and apply current format
@param grossIncome: { string } basic pay of employment income
@param overTime: { string } overtime income
@param bonus: { string } bonus income
@param commissions: { string } commissions income
@param military: { string } military service income
@param  other: { string } other incomes of employment
return the total of given income values
*/
export const calculateTotalIncome = (grossIncome: any, overTime: any, bonus: any, commissions: any, military: any, other: any) => {
  const total = sum(map(unFormatCurrencyFnNum)(
    [
      grossIncome,
      overTime,
      bonus,
      commissions,
      military,
      other
    ]
  ))
  return formatCurrencyFn(defaultTo(0, total.toFixed(2)));
}

export const calculateTotalIncomeInDecimals = (grossIncome: any, overTime: any, bonus: any, commissions: any, military: any, other: any) => {
  const total = sum(map(unFormatCurrencyInCentsFnNum)(
    [
      grossIncome,
      overTime,
      bonus,
      commissions,
      military,
      other
    ]
  ))
  return formatCurrencyWithPrecisionFn(defaultTo(0, total.toFixed(2)));
}

export const concatArgs = (...args: string[]) => {
  return any(isNotEmpty, args) ?
    join("", args) : ""
}

export const sumUp = (values: any[]) => {
  const total = sum(map(unFormatCurrencyFnNum)(
    values
  ))
  return formatRoundedCurrencyFn(total, 2);
}

export const getDisbursementAccountType = (value: any) => {
  return cond([
    [() => includes(value, ['D', 'CEXT']), always('Checking')],
    [() => includes(value, ['S', 'SEXT']), always('Savings')],
    [T, always(value)],
  ])(value)
}

const getEmploymentYears = (startDate: any, endDate: any) => pathOr('', ['years'], getNumberOfYearsAndMonths(startDate, endDate))

const getEmploymentMonths = (startDate: any, endDate: any) => pathOr('', ['months'], getNumberOfYearsAndMonths(startDate, endDate))


export const subtractor = (value1: any = 0, value2: any = 0) => {
  const operand1 = Number(value1)
  const operand2 = Number(value2)
  return toString(subtract(operand1, operand2));
}


/**
 * Subtracts the second number from the first, rounds the result to one decimal place, and returns the result as a string.
 * 
 * @param {number} [value1=0] - The first number. If not provided, defaults to 0. It's converted to a number if it's not already one.
 * @param {number} [value2=0] - The second number, to be subtracted from the first. Defaults to 0 if not provided and is converted to a number.
 * @returns {string} The result of the subtraction, rounded to one decimal place, as a string.
 * 
 * @example
 * // Returns "5.03"
 * console.log(roundedSubtractor(5.45, 0.42));
 * 
 * @example
 * // Returns "-5.00"
 * roundedSubtractor("0", "5"));
 * 
 *  * @example
 * // Returns "-0.83"
 * 
 * console.log(roundedSubtractor("8.83", "8.0"));
 */

export const roundedSubtractor = (value1: any = 0, value2: any = 0) => {
  return subtract(Number(value1), Number(value2)).toFixed(2)

}

export const roundedSumUp = (value1: any = 0, value2: any = 0) => {
  return add(Number(value1), Number(value2)).toFixed(2)
}

export const getRandomDigits = (prefix: string, length: number = 4) => {
  const prefixNumber = !isNaN(Number(prefix)) ? Number(prefix).toString() : '0';
  const randomDigits = Math.floor(Math.random() * Math.pow(10, length)).toString().padStart(length, '0');;
  return take(length, join('', [prefixNumber, randomDigits]))
}

export const appendRandomDigits = (prefix: string, length: number = 4) => {
  const prefixNumber = !isNaN(Number(prefix)) ? Number(prefix).toString() : '0';
  const randomDigits = Math.floor(Math.random() * Math.pow(10, length))
    .toString()
    .padStart(length, '0');

  return join('', [prefixNumber, randomDigits]);
}